import { DataContainer } from "@app/classes/infrastructure/datacontainer";
import { DataContainerService } from "@app/classes/infrastructure/datacontainer.service";
import { IPersistable } from "@app/classes/infrastructure/IPersistable";
import { TransportData } from "@app/classes/infrastructure/transportdata";
import { ValidationResultAccumulator } from "@app/classes/infrastructure/validationresultaccumulator";
import { IdProvider } from "@app/services/idprovider.service";
import { Utils } from "@app/services/utils.service";
import { isNullOrUndefined } from "src/tools";
import { FinancialYearMasterFetchRequest } from "./financialyearmasterfetchrequest";
import { UIUtils } from "@app/services/uiutils.service";
import { PayloadPacketFacade } from "@app/classes/infrastructure/payloadpacket/payloadpacketfacade";
import { ServerCommunicatorService } from "@app/services/server-communicator.service";
import { RequestTypes } from "@app/classes/infrastructure/enums";

export class FinancialYearProps {

  public created_date: string = '';
  public updated_date: string = '';
  public created_by: number = 0;
  public updated_by: number = 0;

  public id: number = 0;
  public from_year: number = 0;
  public to_year: number = 0;
  public short_name: string = '';
  public from_date: string = '';
  public to_date: string = '';

  public readonly is_newly_created: boolean = false;


  private constructor(IsNewlyCreated: boolean) {
    this.is_newly_created = IsNewlyCreated;
  }

  public static Blank() {
    return new FinancialYearProps(true);
  }
}

export class FinancialYear implements IPersistable<FinancialYear> {
  public static readonly mastertablename: string = 'financial_year_master';

  private constructor(public readonly p: FinancialYearProps, public readonly AllowEdit: boolean) {

  }

  public async EnsurePrimaryKeysWithValidValues(): Promise<void> {
    if (this.p.id === undefined || this.p.id === 0) {
      const newids = await IdProvider.GetInstance().GetNextEntityId();
      // const newids = await IdProvider.GetInstance().GetAllocateSingleIds();
      this.p.id = newids[0];
      if (this.p.id <= 0) throw new Error("Cannot assign Id. Please try again");
    }
  }

  public GetEditableVersion(): FinancialYear {
    let newFinancialYear: FinancialYearProps = Utils.GetInstance().DeepCopy(this.p);
    return FinancialYear.CreateInstance(newFinancialYear, true);
  }

  public static CreateNewInstance() {
    return new FinancialYear(FinancialYearProps.Blank(), true);
  }

  public static CreateInstance(data: any, allowEdit: boolean) {
    return new FinancialYear(data as FinancialYearProps, allowEdit);
  }

  public CheckSaveValidity(_td: TransportData, vra: ValidationResultAccumulator): void {
    if (!this.AllowEdit) vra.add('', 'This object is not editable and hence cannot be saved.');
    // if (this.p.financial_year_id <= 0) vra.add('FinancialYearRef', 'FinancialYear cannot be blank. ');
    // if (this.p.CityRef <= 0) vra.add('CityRef', 'City cannot be blank. ');
  }

  public MergeIntoTransportData(td: TransportData) {
    DataContainerService.GetInstance().MergeIntoContainer(td.MainData, FinancialYear.mastertablename, this.p);
  }

  private static m_currentInstance: FinancialYear = FinancialYear.CreateNewInstance();

  public static GetCurrentInstance() {
    return FinancialYear.m_currentInstance;
  }

  public static SetCurrentInstance(value: FinancialYear) {
    FinancialYear.m_currentInstance = value;
  }

  // ********************************************
  public static cacheDataChangeLevel: number = -1;

  public static SingleInstanceFromTransportData(td: TransportData): FinancialYear {
    let dcs = DataContainerService.GetInstance();
    if (dcs.CollectionExists(td.MainData, FinancialYear.mastertablename)) {
      for (let data of dcs.GetCollection(td.MainData, FinancialYear.mastertablename)!.Entries) {
        return FinancialYear.CreateInstance(data, false);
      }
    }

    return null as any;
  }

  public static ListFromDataContainer(cont: DataContainer,
    filterPredicate: (arg0: any) => boolean = null as any,
    sortPropertyName: string = "Name"): FinancialYear[] {
    let result: FinancialYear[] = [];

    let dcs = DataContainerService.GetInstance();

    if (dcs.CollectionExists(cont, FinancialYear.mastertablename)) {
      let coll = dcs.GetCollection(cont, FinancialYear.mastertablename)!;
      let entries = coll.Entries;

      if (!isNullOrUndefined(filterPredicate)) entries = entries.filter(filterPredicate);

      if (sortPropertyName.trim().length > 0) {
        entries = Utils.OrderByPropertyName(entries, sortPropertyName);
      }

      for (let data of entries) {
        result.push(FinancialYear.CreateInstance(data, false));
      }
    }

    return result;
  }

  public static ListFromTransportData(td: TransportData): FinancialYear[] {
    return FinancialYear.ListFromDataContainer(td.MainData);
  }

  public static async FetchTransportData(req: FinancialYearMasterFetchRequest, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let tdRequest = req.FormulateTransportData();
    let pktRequest = PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);

    let tr = await ServerCommunicatorService.GetInstance().sendHttpRequest(pktRequest);
    if (!tr.Successful) {
      if (!isNullOrUndefined(errorHandler)) await errorHandler(tr.Message);
      return null;
    }

    let tdResponse = JSON.parse(tr.Tag) as TransportData;
    return tdResponse;
  }

  public static async FetchInstance(id: number, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new FinancialYearMasterFetchRequest();
    req.financial_year_ids.push(id);

    let tdResponse = await FinancialYear.FetchTransportData(req, errorHandler) as TransportData;
    return FinancialYear.SingleInstanceFromTransportData(tdResponse);
  }

  public static async FetchList(req: FinancialYearMasterFetchRequest, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let tdResponse = await FinancialYear.FetchTransportData(req, errorHandler) as TransportData;
    return FinancialYear.ListFromTransportData(tdResponse);
  }

  public static async FetchEntireList(errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new FinancialYearMasterFetchRequest();
    let tdResponse = await FinancialYear.FetchTransportData(req, errorHandler) as TransportData;
    return FinancialYear.ListFromTransportData(tdResponse);
  }


  public async DeleteInstance(successHandler: () => Promise<void> = null!, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let tdRequest = new TransportData();
    tdRequest.RequestType = RequestTypes.Deletion;

    this.MergeIntoTransportData(tdRequest);
    let pktRequest = PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);

    let tr = await ServerCommunicatorService.GetInstance().sendHttpRequest(pktRequest);
    if (!tr.Successful) {
      if (!isNullOrUndefined(errorHandler)) await errorHandler(tr.Message);
    }
    else {
      if (!isNullOrUndefined(successHandler)) await successHandler();
    }
  }

}
